import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithRef } from 'react';

import { cn } from '@core/utils/cn';

/**
 * Every visual decision for a button lives here. Feature code picks a variant; it never
 * writes `bg-glass backdrop-blur-glass ring-glass-stroke` itself.
 *
 * Designed to be dropped into any Base UI trigger via the `render` prop:
 *
 *     <Dialog.Trigger render={<Button variant="primary" />}>New geofence</Dialog.Trigger>
 *
 * which is why it spreads every prop and forwards its ref.
 */
export const buttonVariants = cva(
  [
    'relative inline-flex items-center justify-center gap-2 whitespace-nowrap select-none',
    'font-medium tracking-[-0.01em]',
    'transition-[transform,background-color,box-shadow,opacity,filter] duration-200 ease-spring',
    // Press is a scale, not a colour change -- that is what makes it feel native.
    'active:scale-[0.97]',
    'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
    'disabled:pointer-events-none disabled:opacity-40',
    // Base UI signals state through data-* on parts that are not real <button>s,
    // so :disabled alone is not enough.
    'data-disabled:pointer-events-none data-disabled:opacity-40',
    'motion-reduce:transition-none motion-reduce:active:scale-100',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-fg shadow-raised hover:brightness-110',
        secondary: 'bg-accent-tint text-accent hover:bg-accent/20',
        glass: 'glass text-fg hover:bg-glass-strong',
        ghost: 'text-fg-muted hover:bg-fill-subtle hover:text-fg',
        danger: 'bg-danger text-white shadow-raised hover:brightness-110',
      },
      size: {
        sm: 'h-8 px-3 text-[13px]',
        md: 'h-10 px-4 text-[15px]',
        lg: 'h-12 px-5 text-[17px]',
        icon: 'size-10 p-0',
        'icon-sm': 'size-8 p-0',
      },
      shape: {
        pill: 'rounded-pill',
        rounded: 'rounded-lg',
      },
    },
    defaultVariants: { variant: 'glass', size: 'md', shape: 'pill' },
  },
);

export interface ButtonProps
  extends ComponentPropsWithRef<'button'>, VariantProps<typeof buttonVariants> {}

export function Button({ className, variant, size, shape, type, ...props }: ButtonProps) {
  return (
    <button
      // Buttons inside a <form> default to submit, which fires the form by accident
      // roughly once per project. Opt in explicitly instead.
      type={type ?? 'button'}
      className={cn(buttonVariants({ variant, size, shape }), className)}
      {...props}
    />
  );
}
